<script setup lang="ts">
/**
 * Diseases (medical absences) table tab. Read-only.
 *
 * Infinite-scrolls `/diseases` then batch-resolves client names via POST
 * `/persons/clients/by-ids`. From/To dates are formatted in UTC (not the
 * browser timezone) because backend timestamps are midnight-UTC — using the
 * local timezone would shift the displayed calendar date in western timezones.
 *
 * `formatDiseaseDate` encapsulates the UTC formatting and returns `undefined`
 * for missing/unparseable input so the cell shows its muted placeholder.
 */
import { computed, markRaw, ref } from 'vue'
import { useInfiniteQuery, useQuery } from '@tanstack/vue-query'
import DynamicTable from '@/components/table/DynamicTable.vue'
import type { Column } from '@/components/table/DynamicTable.vue'
import AddButton from '@/components/table/AddButton.vue'
import RowIndexCell from '@/components/table/cell/RowIndexCell.vue'
import ClientNameCell from '@/components/table/cell/ClientNameCell.vue'
import TextCell from '@/components/table/cell/TextCell.vue'
import { useIsVisible } from '@/composables/useIsVisible'
import { fetchDiseases } from '@/api/diseases'
import { fetchClientsByIds } from '@/api/clients'
import { diseasesPollingInterval } from '@/config/api'
import { defaultLocaleForDate } from '@/config/date'
import { formatName } from '@/utils/formatName'

interface DiseaseRow {
  // Row for the diseases table, mapped from GET `/diseases`.

  rowNumber: number
  // 1-based index over all loaded pages (same convention as the other tabs).

  personName: string
  // Client display name resolved via POST `/persons/clients/by-ids`; falls back to `Person #<id>` while the lookup is pending or when the id is unknown.

  fromDate: string
  // Pre-formatted `from_date` for display (e.g. `Nov 1, 2025`).

  toDate: string
  // Pre-formatted `to_date` for display.

  notes: string
  // Free-form notes copied verbatim from the API (may be empty).
}

/**
 * Formats an ISO datetime as a locale-aware, date-only label for the From/To
 * columns. Disease boundaries from the backend are midnight-UTC (e.g.
 * `2023-11-01T00:00:00Z`) so we intentionally format in UTC: using the browser
 * timezone would drift the displayed calendar date by one in timezones west of
 * UTC (the user would see `2023-10-31`). `undefined` is returned for missing
 * or unparseable input so the cell can render its muted placeholder.
 */
function formatDiseaseDate(iso: string | null | undefined): string | undefined {
  if (typeof iso !== 'string' || iso.trim() === '') return undefined
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return undefined
  return new Intl.DateTimeFormat(defaultLocaleForDate.value, {
    dateStyle: 'medium',
    timeZone: 'UTC',
  }).format(d)
}

// Column widths sum to ~48rem (≈ `--width-middle-column` minus the
// article's 4rem horizontal padding) so this table has the same total
// width as every other tab's table; see `App.vue` for context.
const columns: Column<DiseaseRow>[] = [
  {
    key: 'rowNumber',
    label: '#',
    component: markRaw(RowIndexCell),
    props: (row) => ({ index: row.rowNumber }),
    width: '3rem',
    align: 'center',
  },
  {
    key: 'personName',
    label: 'Client',
    // Use the same cell type as `ClientsTab` so client-name typography
    // (font, size, weight) is consistent across tabs. Only `name` is
    // passed; without `gender` / `startDate`, `ClientNameCell` skips its
    // muted meta line and collapses to a single name line.
    component: markRaw(ClientNameCell),
    props: (row) => ({ name: row.personName }),
    width: '14rem',
  },
  {
    key: 'fromDate',
    label: 'From',
    component: markRaw(TextCell),
    props: (row) => ({ value: row.fromDate, emptyPlaceholder: '–' }),
    width: '6rem',
  },
  {
    key: 'toDate',
    label: 'To',
    component: markRaw(TextCell),
    props: (row) => ({ value: row.toDate, emptyPlaceholder: '–' }),
    width: '6rem',
  },
  {
    key: 'notes',
    label: 'Notes',
    component: markRaw(TextCell),
    props: (row) => ({ value: row.notes, emptyPlaceholder: '–' }),
    width: '19rem',
  },
]

const {
  data: queryData,
  isPending,
  isError,
  error,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
} = useInfiniteQuery({
  queryKey: ['diseases'],
  queryFn: ({ pageParam }) => fetchDiseases(pageParam),
  initialPageParam: undefined as number | undefined,
  getNextPageParam: (lastPage) =>
    lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
  refetchInterval: diseasesPollingInterval,
  refetchIntervalInBackground: false,
})

/**
 * Sorted, de-duplicated list of `person_id` values across every loaded
 * diseases page. Sorting makes the query key stable (so identical id sets
 * across refetches share the same cache entry) and scanning pages lets the
 * Client column stay filled as `fetchNextPage` appends rows.
 */
const personIds = computed<string[]>(() => {
  const pages = queryData.value?.pages
  if (!pages?.length) return []
  const set = new Set<string>()
  for (const page of pages) {
    for (const d of page.items ?? []) {
      if (d.person_id) {
        set.add(d.person_id)
      }
    }
  }
  return Array.from(set).sort()
})

/**
 * Resolves `person_id` → display name via a single POST batch. The query
 * key includes the sorted ids so a new request is issued only when the id
 * set actually changes (e.g. after `fetchNextPage` loads new persons),
 * and `enabled` keeps it idle until at least one disease row is loaded.
 */
const { data: clientsData } = useQuery({
  queryKey: ['clientsByIds', personIds],
  queryFn: () => fetchClientsByIds(personIds.value),
  enabled: computed(() => personIds.value.length > 0),
  refetchInterval: diseasesPollingInterval,
  refetchIntervalInBackground: false,
})

const personNameById = computed<Map<string, string>>(() => {
  const map = new Map<string, string>()
  const items = clientsData.value?.items
  if (!items?.length) return map
  for (const c of items) {
    const raw = typeof c.name === 'string' ? c.name.trim() : ''
    if (raw !== '') {
      map.set(c.id, formatName(raw))
    }
  }
  return map
})

/**
 * Table rows derived from the infinite-query cache: every loaded page is
 * flattened in order so "load more" appends rows without resetting indices.
 *
 * `rowNumber` is a 1-based running index across all pages (incremented once
 * per disease, not per page), so the "#" column stays stable while scrolling.
 *
 * `personName` falls back to `Person #<id>` while the by-ids lookup is
 * pending (or when the backend omits that id because the person is not a
 * client); this matches `PaymentsTab`'s unresolved-name convention.
 */
const data = computed<DiseaseRow[]>(() => {
  const pages = queryData.value?.pages
  if (!pages?.length) return []
  let index = 0
  return pages.flatMap((page) =>
    (page.items ?? []).map((d) => {
      index += 1
      const notes = typeof d.notes === 'string' ? d.notes.trim() : ''
      return {
        rowNumber: index,
        personName: personNameById.value.get(d.person_id) ?? `Person #${d.person_id}`,
        fromDate: formatDiseaseDate(d.from_date) ?? '',
        toDate: formatDiseaseDate(d.to_date) ?? '',
        notes,
      }
    }),
  )
})

const headerRef = ref<HTMLElement | null>(null)
const isHeaderVisible = useIsVisible(headerRef)

function onAddDisease(): void {}
</script>

<template>
  <article>
    <div ref="headerRef" class="tab-header">
      <h1>Diseases</h1>
      <AddButton label="Add disease" @click="onAddDisease" />
    </div>
    <p v-if="isPending" class="text-tufte-muted">Loading diseases…</p>
    <p v-else-if="isError" class="text-red-600">
      Failed to load diseases: {{ (error as Error)?.message ?? 'Unknown error' }}
    </p>
    <template v-else>
      <DynamicTable
        class="diseases-table"
        :columns="columns as Column<unknown>[]"
        :data="data"
        :has-next-page="hasNextPage"
        :is-fetching-next-page="isFetchingNextPage"
        :on-load-more="fetchNextPage"
        :on-add="onAddDisease"
        :compact-add-visible="!isHeaderVisible"
        storage-key="diseases"
      />
    </template>
  </article>
</template>

<style scoped>
.tab-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}

/*
 * The table stretches to fill the middle column (`width: 100%` default) so
 * that extra horizontal space the three-column layout gives the middle
 * column is reflected in the table itself. The Notes column is still capped
 * by its `maxWidth` so very long messages do not explode the layout.
 */

/*
 * Keep the `From` / `To` date labels on a single line. Without this,
 * medium-format dates like `30 Nov 2023` (~11 chars at the Tufte serif
 * scale) exceed the 8rem column target and wrap across two lines, which
 * makes the date range harder to scan than the compact notes next to it.
 * With `white-space: nowrap` the column auto-grows to fit the longest
 * label and every row stays one line tall.
 */
.diseases-table :deep(tbody tr > td:nth-child(3)),
.diseases-table :deep(tbody tr > td:nth-child(4)) {
  white-space: nowrap;
}

/*
 * Notes are free-form sentences and dominate the row visually at the default
 * `--text-tufte-base`. Shrinking the last body cell to `--text-tufte-sm`
 * with a tighter line-height keeps multi-line notes compact while the
 * Client / From / To columns retain the standard table scale (mirrors
 * RulesTab's treatment of the `Rule` column).
 */
.diseases-table :deep(tbody tr > td:nth-child(5)) {
  font-size: var(--text-tufte-sm);
  line-height: 1.35;
}
</style>
